import React, { useContext } from "react";
import Helmet from "react-helmet";
import { MpvProvider } from "../utils/MpvProvider";
import { createBffRequest } from "../services/BFFService";
import { ContentfulCloudinaryImageComponent, Seo, Tab } from "../types/ContentfulTypes";
import { ProductBuilderOptions } from "../types/ProductBuilderTypes";
import { RatingReviewData } from "@vp/rating-reviews-component/build/components/ReviewDisplay";
import PageConfig from "../utils/PageConfig";
import { CloudinaryVideoMedia } from "../types/CloudinaryTypes";
import { PageStaticDataContext } from "../contexts/PageStaticDataContextProvider";
interface BFFPrefetcherProps {
  productBuilderOptions: ProductBuilderOptions;
  seo?: Seo;
  defaultVatIncl: boolean;
  pageUrl: string;
  reviews: RatingReviewData;
  heroGallery: (ContentfulCloudinaryImageComponent | CloudinaryVideoMedia)[];
  tabs?: Tab[];
}

export const ProductPageBFFPreloader = (props: BFFPrefetcherProps) => {
  const { productBuilderOptions, seo, defaultVatIncl, pageUrl, reviews, heroGallery, tabs } = props;
  const { locale, mpv, canonicalDomain } = useContext(PageStaticDataContext);
  const mpvProvider = new MpvProvider(mpv);
  const preselectedOptions = mpvProvider.getPreselectedOptions(productBuilderOptions);

  const isSSR = PageConfig.isSSR;
  const initialBffParams =
    typeof window !== "undefined" ? window.preload?.preloadBffParams : undefined;
  const shouldPrefetchSSR = isSSR && !!initialBffParams;

  let prefetchUrl: string = "";
  if (shouldPrefetchSSR) {
    const preloadBffParams = {
      ...initialBffParams!,
      selections: {
        ...initialBffParams!.selections,
        ...preselectedOptions,
      },
      requestor: "product-page-v2",
    };

    prefetchUrl = createBffRequest(preloadBffParams);

    const faqsTab = tabs?.find(
      (tab: any) => tab.__typename === "ContentfulTabFaqs" && tab?.faQs && tab?.faQs.length > 0
    );

    // A separate preload link will be generated from the `clientSideBundle`
    // It will use the following global variables to generate a client-side preload
    const inlineIIFEGlobalVarScript = `
    (function() {
      window.preload = window.preload || {};
      window.preload.locale = '${locale}';
      window.preload.mpv = ${JSON.stringify(mpv)};
      window.preload.preloadBffParams = ${JSON.stringify(preloadBffParams)};
      window.preload.preselectedOptions = ${JSON.stringify(preselectedOptions)};
      window.preload.defaultPrefetchUrl = '${prefetchUrl}';
      window.preload.pageUrl = '${pageUrl}';
      window.preload.seo = ${JSON.stringify(seo)};
      window.preload.canonicalDomain = '${canonicalDomain}';
      window.preload.defaultVatIncl = ${defaultVatIncl};
      window.preload.reviews = ${JSON.stringify(reviews)};
      window.preload.heroGallery = ${JSON.stringify(heroGallery)};
      window.preload.faqs = ${JSON.stringify(faqsTab?.faQs || [])};
    })();`;

    return (
      <Helmet>
        <script type="text/javascript">{inlineIIFEGlobalVarScript}</script>
        <link
          rel="preload"
          as="fetch"
          type="json"
          crossOrigin="anonymous"
          href={prefetchUrl}
        ></link>
      </Helmet>
    );
  }

  return <></>;
};

export default ProductPageBFFPreloader;
